/**
 * `navegar` — a mão do modelo na barra de navegação ([T015] / US-4).
 *
 * Espelho do `ToolNavegar` do backend (`tools/tools_de_front.py`): lá está o
 * `TOOL_SCHEMA` que o modelo lê (`destino`, `params`, `modo`); aqui, o que
 * acontece no browser. O `output` devolvido é o que volta ao modelo no
 * `tool_result`, e o formato é o que a descrição da tool PROMETE a ele —
 * `{aberto, rota, motivo}`. Divergir daqui é fazer o modelo narrar ao usuário
 * um desfecho que não aconteceu.
 *
 * ★ `secao` (14/09/2026): a ABA dentro do módulo, em texto livre ("Leads",
 * "Transcrições"). Resolvida contra as abas reais do módulo
 * (`secoes-do-modulo.js`) ANTES de empurrar; com match o output ganha
 * `{secao, rota}`, sem match a entrada do módulo abre como sempre e o output
 * diz `{secao_encontrada: false, secoes_disponiveis}` — o modelo corrige no
 * turno seguinte em vez de narrar uma aba que não abriu. Sem `secao`, o
 * output é o de sempre, byte a byte.
 */
import {
  rotaDoDestino,
  rotaExisteNoRouter,
  rotaInternaPermitida,
  destinosConhecidos,
  temContextoDeNavegacao,
  podeAcessar,
  empurrarRota,
  ABRIR_TELA_EM_NOVA_ABA,
  MOTIVO_SEM_CONTEXTO,
} from './catalogo'
import { secoesDoModulo, resolverSecao, rotulosDasSecoes } from './secoes-do-modulo'

export const NOME_DA_TOOL = 'navegar'
export const LABEL = 'Abrir tela'

const recusar = (motivo) => ({ ok: false, motivo: motivo })

/**
 * `input.params`, tolerante ao formato — objeto (o caso de sempre) OU uma
 * STRING JSON (spec-016 [T039], QA it.4 G-6). A `instrucao` que
 * `preparar_ensaio_fee_fixo` devolve pede ao modelo para montar `params`
 * como a `query` MESCLADA com `{rota: ROTA_DO_ENSAIO}` — em texto livre, via
 * `repr()` do Python, não `tool_choice` estruturado — e o modelo pode
 * serializar o resultado como string em vez de objeto. Sem esta tolerância,
 * `paramsRecebidos.rota` era `undefined` (strings não têm essa propriedade),
 * a allow-list nunca casava, e o guard caía silenciosamente na raiz do
 * módulo — o sintoma medido no QA (`/fee-fixo/clientes` em vez do ensaio).
 * String que não é JSON válido, ou que não vira objeto, devolve `{}` — o
 * MESMO comportamento de sempre (cai na raiz do módulo, nunca lança).
 */
function normalizarParams(params) {
  if (params && typeof params === 'object' && !Array.isArray(params)) return params
  if (typeof params === 'string' && params.trim()) {
    try {
      const parsed = JSON.parse(params)
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed
    } catch (erro) {
      // String que não é JSON não é um `params` válido — cai no `{}` abaixo.
    }
  }
  return {}
}

/**
 * Pode navegar? Devolve a rota resolvida junto — quem executa não resolve de
 * novo, e assim guard e handler não podem discordar sobre o destino.
 *
 * @param {Object} input  `{destino, params}` do `tool-input-available`.
 * @param {Object} ctx    `{router, store, canAccessRoute, vm}`.
 * @returns {{ok: boolean, motivo?: string, rota?: Object}}
 */
export function guard(input, ctx) {
  const dados = input || {}
  if (!temContextoDeNavegacao(ctx)) return recusar(MOTIVO_SEM_CONTEXTO)

  const destino = dados.destino
  if (!destino) {
    return recusar(
      'nenhum destino informado. Destinos válidos: ' +
        destinosConhecidos().join(', ') + '.'
    )
  }

  const paramsRecebidos = normalizarParams(dados.params)
  const resolvido = resolverNomeDaRota(destino, ctx, paramsRecebidos, dados.secao)
  if (!resolvido) {
    return recusar(
      'o destino "' + destino + '" não existe no catálogo de telas da ' +
        'plataforma. Destinos válidos: ' + destinosConhecidos().join(', ') + '.'
    )
  }
  // ★ A rota existe no router ANTES de empurrar (QA-G-1, round 2). Sem esta
  // porta, destino que resolve para um `name` inexistente virava push, e o
  // vue-router mandava o consultor ao `/404` — a recusa chegava DEPOIS, com ele
  // já na tela de erro. Recusar aqui é o que garante zero mudança de rota.
  // Roda contra o `name` FINAL (a rota interna, quando houver) — checar só o
  // módulo deixaria uma rota interna removida numa versão futura passar batido.
  if (!rotaExisteNoRouter(ctx, resolvido.name)) {
    return recusar(
      'a tela "' + destino + '" não existe nesta versão da plataforma, então ' +
        'não abri nada. Destinos válidos: ' + destinosConhecidos().join(', ') + '.'
    )
  }

  if (!podeAcessar(ctx, resolvido.name)) {
    return recusar(
      'o escritório não tem acesso à tela "' + destino + '", então ela não pode ' +
        'ser aberta.'
    )
  }

  return {
    ok: true,
    rota: montarRotaFinal(resolvido.name, resolvido.rotaInterna, paramsRecebidos),
    ...resolvido.secao,
  }
}

/**
 * A aba pedida em `secao`, resolvida contra as abas REAIS do módulo — ou
 * `null` quando nada casa (e aí vão as disponíveis, para o modelo corrigir).
 * `undefined` quando `secao` não veio: o guard não acrescenta chave nenhuma,
 * e o output continua o de sempre.
 *
 * A rota INTERNA da allow-list (`params.rota`) vence: ela é instrução do
 * BACKEND (`preparar_ensaio_fee_fixo`), mais específica do que o rótulo.
 */
function resolverSecaoPedida(secao, nomeDoModulo, rotaInterna, ctx) {
  if (typeof secao !== 'string' || !secao.trim() || rotaInterna) return undefined
  const secoes = secoesDoModulo(nomeDoModulo, ctx)
  const achada = resolverSecao(secao, secoes)
  if (achada) return { secao: achada }
  return { secao: null, secoesDisponiveis: rotulosDasSecoes(secoes) }
}

/**
 * O `name` FINAL do destino, e se veio de uma rota INTERNA — `null` quando o
 * destino nem existe no catálogo de módulos. Extraído do `guard` só para não
 * estourar o `max-statements` do ESLint (a lógica é a mesma).
 *
 * ★ A rota INTERNA de um destino de módulo (spec-016 [T021], F-1 da review de
 * front): a tool `preparar_ensaio_fee_fixo` devolve `destino='fee-fixo'` +
 * `params={..., rota:'fee-fixo.roleplay.ensaio'}` — sem este ramo, o `name`
 * final seria SEMPRE a raiz do módulo (`fee-fixo`, que redireciona para
 * `fee-fixo.guia`), e a query com cliente/etapa/personagem nunca chegaria à
 * tela do ensaio. Fora da allow-list, `rotaInterna` é `null` e o
 * comportamento é o de sempre — nunca uma rota livre vinda do modelo.
 */
function resolverNomeDaRota(destino, ctx, paramsRecebidos, secaoPedida) {
  // O `ctx` vai junto: três destinos do catálogo (Comissões, Tesouraria,
  // Consolidador) resolvem a tela real pelas flags do escritório, e sem ele o
  // `routeName` cru é o rótulo do módulo, não uma rota.
  const nomeDoModulo = rotaDoDestino(destino, ctx)
  if (!nomeDoModulo) return null
  const rotaInterna = rotaInternaPermitida(destino, paramsRecebidos.rota)
  const secao = resolverSecaoPedida(secaoPedida, nomeDoModulo, rotaInterna, ctx)
  const aba = secao && secao.secao ? secao.secao.name : null
  return { name: rotaInterna || aba || nomeDoModulo, rotaInterna: rotaInterna, secao: secao }
}

/**
 * A forma final de `rota` — `{name, params, query}` para rota interna,
 * `{name, params}` (formato de sempre) para rota de módulo.
 */
function montarRotaFinal(name, rotaInterna, paramsRecebidos) {
  if (!rotaInterna) return { name: name, params: paramsRecebidos }
  return { name: name, params: {}, query: paramsSemRota(paramsRecebidos) }
}

/** `params` sem a chave `rota` — o resto vira `query` da rota interna. */
function paramsSemRota(params) {
  const resto = {}
  Object.keys(params).forEach((chave) => {
    if (chave !== 'rota') resto[chave] = params[chave]
  })
  return resto
}

/**
 * A navegação chegou na tela pedida?
 *
 * ★ **Chegar num FILHO da rota pedida é chegar** (QA-G-5). Rota com `redirect`
 * declarado nunca termina no próprio nome: `task` redireciona para
 * `/task/overview`, então `currentRoute.name` é `task.overview` e a comparação
 * por nome cru acusava desvio sobre uma navegação que deu certo. O QA leu o
 * cartão dizendo *"a tela não abriu… o escritório não tem essa tela contratada,
 * ou o usuário não tem permissão"* com a tela CERTA aberta na frente dele — e a
 * IA repetia o diagnóstico falso. Dizer a alguém que ele não tem permissão para
 * a tela que está vendo é chamado de suporte na certa.
 *
 * `matched` é a cadeia de records casados, do pai ao filho: quem pediu `task` e
 * parou em `task.overview` tem `task` nela. Quem foi desviado para `/404` não
 * tem — e aí o diagnóstico continua valendo.
 *
 * @returns {boolean} `true` também quando o router não expõe a rota corrente:
 *                    sem o dado não há o que conferir, e inventar uma recusa é
 *                    pior que a afirmação otimista que havia antes.
 */
function chegouNaRotaPedida(ctx, name) {
  const router = ctx && ctx.router
  const atual = router && router.currentRoute
  if (!atual) return true

  const cadeia = atual.matched
  if (Array.isArray(cadeia) && cadeia.length) {
    return cadeia.some((registro) => registro && registro.name === name)
  }

  // Sem `matched` (dublê antigo, superfície exótica): resta o nome.
  if (!atual.name) return true
  return atual.name === name
}

/** Onde parou — só para a MENSAGEM da recusa. */
function nomeDaRotaCorrente(ctx) {
  const router = ctx && ctx.router
  const atual = router && router.currentRoute
  return (atual && atual.name) || 'outra tela'
}

/**
 * O que o output diz sobre a `secao` — nada quando ela não foi pedida (o
 * output fica o de sempre), `{secao}` com match, e
 * `{secao_encontrada: false, secoes_disponiveis}` sem: a entrada do módulo
 * abriu, mas não a aba, e o modelo precisa saber disso para não narrar
 * "abri a aba de leads" com o Bem-vindo na tela.
 */
function desfechoDaSecao(veredito) {
  if (veredito.secao === undefined) return {}
  if (veredito.secao) return { secao: veredito.secao.rotulo }
  return { secao_encontrada: false, secoes_disponiveis: veredito.secoesDisponiveis }
}

const MOTIVO_DESVIADA = (destino, onde) =>
  'a tela "' + destino + '" não abriu: a plataforma redirecionou para "' +
  onde + '". Em geral é porque o escritório não tem essa tela contratada, ou ' +
  'o usuário não tem permissão para ela.'

/**
 * Abre a tela. Roda SEMPRE o guard antes: o chamador já o rodou, mas um handler
 * que confia no chamador é um handler que navega sem checar no dia em que
 * alguém o chamar de outro lugar.
 *
 * @returns {Promise<{aberto: boolean, rota: ?Object, motivo?: string}>}
 */
export function executar(input, ctx) {
  const veredito = guard(input, ctx)
  if (!veredito.ok) {
    return Promise.resolve({
      aberto: false,
      rota: null,
      motivo: veredito.motivo,
      recusado: true,
    })
  }

  return empurrarRota(ctx, veredito.rota, ABRIR_TELA_EM_NOVA_ABA).then((resultadoAbertura) => {
    // ★ O navegador BLOQUEOU o pop-up (feedback do owner, 04/09) — o caso é a
    // navegação AUTOMÁTICA, sem gesto do usuário. `recusado: true` é o canal
    // certo: o motivo viaja ao modelo (ele pediu a ação e ela não aconteceu) E
    // o cartão sai do estado "Aberto" para mostrar o botão de fallback, que o
    // CLIQUE do usuário abre sem ser bloqueado.
    if (resultadoAbertura && resultadoAbertura.bloqueado) {
      return {
        aberto: false,
        bloqueado: true,
        href: resultadoAbertura.href,
        rota: veredito.rota,
        motivo: resultadoAbertura.motivo,
        recusado: true,
      }
    }
    // ★ Em nova aba não há o que conferir AQUI: a navegação acontece numa
    // outra aba, desvinculada da atual (`aba.opener = null`) — o router
    // desta janela nunca muda de rota, então `chegouNaRotaPedida` comparava a
    // aba de sempre (`ia-imersivo`) contra o destino pedido e acusava desvio
    // em TODO clique, sempre. `empurrarRota` já rejeita quando não consegue
    // resolver o `href`; chegar aqui é a aba ter aberto.
    if (ABRIR_TELA_EM_NOVA_ABA || chegouNaRotaPedida(ctx, veredito.rota.name)) {
      return { aberto: true, ...desfechoDaSecao(veredito), rota: veredito.rota }
    }
    const onde = nomeDaRotaCorrente(ctx)
    return {
      aberto: false,
      rota: veredito.rota,
      rota_final: onde,
      motivo: MOTIVO_DESVIADA(input.destino, onde),
      recusado: true,
    }
  })
}

export default { nome: NOME_DA_TOOL, label: LABEL, guard: guard, executar: executar }
